/* =================================================================
   WernerArt — Animations
   - Scroll-Reveal mit IntersectionObserver
   - Polaroid-Stack Stagger
   - Hero-Bild-Crossfade beim Scrollen (optional)
   ================================================================= */

(function () {
  'use strict';

  // Reduzierte Bewegung respektieren
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // =================================================================
  // 1 + 2. SCROLL-REVEAL — startet erst nach dem Ladescreen
  // =================================================================
  function initReveal() {
    // Bereits im DOM markierte [data-wa-reveal] Elemente beobachten
    const revealElements = document.querySelectorAll('[data-wa-reveal]');
    if (revealElements.length > 0 && 'IntersectionObserver' in window && !prefersReducedMotion) {
      const revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-revealed');
              revealObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -50px 0px' }
      );
      revealElements.forEach((el) => revealObserver.observe(el));
    } else if (prefersReducedMotion) {
      revealElements.forEach((el) => el.classList.add('is-revealed'));
    }

    // Auto-Markup für häufige Elemente + beobachten
    const autoTargets = [
      { sel: '.title-wrapper-with-link',      type: 'fade-up'    },
      { sel: '.collection__title',            type: 'fade-up'    },
      { sel: '.product-grid .grid__item',     type: 'fade-up'    },
      { sel: '.collection-list__item',        type: 'fade-up'    },
      { sel: '.rich-text__wrapper',           type: 'fade-up'    },
      { sel: '.wa-polaroid-card',             type: 'scale-in'   },
      { sel: '.wa-usp-card',                  type: 'scale-in'   },
      { sel: '.wa-newsletter__inner',         type: 'fade-up'    },
      { sel: '.wa-stimmen__header',           type: 'fade-up'    },
      { sel: '.wa-artist-editorial__media',   type: 'fade-right' },
      { sel: '.wa-artist-editorial__text',    type: 'fade-left'  },
    ];

    autoTargets.forEach(({ sel, type }) => {
      document.querySelectorAll(sel).forEach((el, index) => {
        if (el.hasAttribute('data-wa-reveal')) return;
        el.setAttribute('data-wa-reveal', '');
        el.setAttribute('data-wa-reveal-type', type);
        el.style.setProperty('--wa-reveal-delay', `${(index % 4) * 130}ms`);
      });
    });

    document.querySelectorAll('.wa-stimmen__item').forEach((el, index) => {
      if (el.hasAttribute('data-wa-reveal')) return;
      el.setAttribute('data-wa-reveal', '');
      el.setAttribute('data-wa-reveal-type', index % 2 === 0 ? 'fade-right' : 'fade-left');
      el.style.setProperty('--wa-reveal-delay', `${index * 200}ms`);
    });

    if (!prefersReducedMotion && 'IntersectionObserver' in window) {
      const newRevealEls = document.querySelectorAll('[data-wa-reveal]:not(.is-revealed)');
      const newObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-revealed');
              newObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -50px 0px' }
      );
      newRevealEls.forEach((el) => newObserver.observe(el));
    } else if (prefersReducedMotion) {
      document.querySelectorAll('[data-wa-reveal]').forEach((el) => el.classList.add('is-revealed'));
    }
  }

  function scheduleReveal() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initReveal, { once: true });
    } else {
      initReveal();
    }
  }

  // wa-rd-sections.css setzt body { opacity: 0 } als Entrance-State.
  // Diese Funktion fügt .wa-page-loaded hinzu → body wird sichtbar.
  function triggerPageLoaded() {
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        document.body.classList.add('wa-page-loaded');
      });
    });
  }

  // Loader wurde bereits in dieser Session gezeigt → sofort sichtbar machen
  // Erster Besuch → auf wa-loader-done warten (Loader dispatcht dieses Event)
  if (sessionStorage.getItem('wa-loader-shown')) {
    triggerPageLoaded();
    scheduleReveal();
  } else {
    document.addEventListener('wa-loader-done', function () {
      triggerPageLoaded();
      scheduleReveal();
    }, { once: true });
    // Fallback: falls wa-loader-done nie feuert (z.B. JS-Fehler im Loader)
    setTimeout(triggerPageLoaded, 4500);
  }

  // =================================================================
  // 3. POLAROID-SCROLL-DRIVEN — beim Scrollen durchblättern
  // =================================================================
  function initPolaroidScroll() {
    const sections = document.querySelectorAll('[data-wa-polaroid-section]');

    sections.forEach((section) => {
      const cards = section.querySelectorAll('[data-wa-polaroid-card]');
      const cardCount = cards.length;
      if (cardCount === 0) return;

      // Initial: erst Reveal-Klasse setzen wenn Section sichtbar
      const revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              section.classList.add('is-revealed');
            }
          });
        },
        { threshold: 0.1 }
      );
      revealObserver.observe(section);

      // Counter-Element für Magazin-Indikator
      const currentCardEl = section.querySelector('[data-current-card]');
      const totalCardEl = section.querySelector('.wa-polaroid-indicator__total');

      // Scroll-Progress berechnen
      let ticking = false;
      let lastActive = -1;
      const updateProgress = () => {
        const rect = section.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        // Scrollbare Distanz innerhalb der Section
        const scrollableHeight = rect.height - viewportHeight;

        // Wie weit haben wir innerhalb der Section gescrollt? 0 = oben, 1 = unten
        const scrolled = -rect.top;
        const progress = Math.max(0, Math.min(1, scrolled / scrollableHeight));

        // Diskrete Stufen — eine pro Karte
        const stepSize = 1 / cardCount;
        let activeCard = Math.floor(progress / stepSize);
        if (activeCard >= cardCount) activeCard = cardCount - 1;

        section.setAttribute('data-active-card', activeCard);

        // Counter aktualisieren
        if (activeCard !== lastActive && currentCardEl) {
          const display = (activeCard + 1).toString().padStart(2, '0');
          currentCardEl.textContent = display;
          lastActive = activeCard;
        }

        // Letzte Karte erreicht → Pfeil ausblenden
        section.setAttribute('data-last-card', activeCard >= cardCount - 1 ? 'true' : 'false');

        ticking = false;
      };

      const onScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(updateProgress);
          ticking = true;
        }
      };

      // Nur tracken wenn Section sichtbar — schont Performance
      const trackObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              window.addEventListener('scroll', onScroll, { passive: true });
              updateProgress();
            } else {
              window.removeEventListener('scroll', onScroll);
            }
          });
        },
        { rootMargin: '100px' }
      );
      trackObserver.observe(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPolaroidScroll);
  } else {
    initPolaroidScroll();
  }

  // =================================================================
  // 4. SMOOTH-CLOSE — Search Modal mit Slide-Up beim Schließen
  // =================================================================
  function initSmoothModalClose() {
    const searchDetails = document.querySelectorAll('.header__search details, details-modal details');

    searchDetails.forEach((details) => {
      const modal = details.querySelector('.search-modal');
      const closeButton = details.querySelector('.search-modal__close-button, .wa-search-close');
      const overlay = details.querySelector('.modal-overlay');
      const summary = details.querySelector('summary');

      if (!modal) return;

      const smoothClose = (e) => {
        if (!details.open) return;
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        // Animation-Klasse hinzufügen
        details.classList.add('is-closing');
        // Nach Animation: details wirklich schließen + Body-Scroll wieder freigeben
        setTimeout(() => {
          details.open = false;
          details.classList.remove('is-closing');
          document.documentElement.classList.remove('wa-search-open');
        }, 420);
      };

      // Beim Öffnen: Body-Scroll sperren
      details.addEventListener('toggle', () => {
        if (details.open) {
          document.documentElement.classList.add('wa-search-open');
        } else {
          document.documentElement.classList.remove('wa-search-open');
        }
      });

      if (closeButton) {
        closeButton.addEventListener('click', smoothClose);
      }
      if (overlay) {
        overlay.addEventListener('click', smoothClose);
      }
      // Summary (Suche-Toggle): nur smooth-close wenn bereits offen
      if (summary) {
        summary.addEventListener('click', (e) => {
          if (details.open) {
            smoothClose(e);
          }
        });
      }

      details.addEventListener('wa-close', smoothClose);
    });

    // ESC — einmalig statt pro Element
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      searchDetails.forEach((d) => {
        if (d.open) d.dispatchEvent(new Event('wa-close'));
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSmoothModalClose);
  } else {
    initSmoothModalClose();
  }

  // =================================================================
  // 5. VERTIKALE LABELS — beim Scrollen ausblenden
  // =================================================================
  function initVerticalMarks() {
    const marks = document.querySelector('[data-wa-vertical-marks]');
    if (!marks) return;

    const threshold = window.innerHeight * 0.6;
    let ticking = false;

    const update = () => {
      const scrolled = window.scrollY > threshold;
      marks.setAttribute('data-scrolled', scrolled ? 'true' : 'false');
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });

    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVerticalMarks);
  } else {
    initVerticalMarks();
  }

  // =================================================================
  // 6. LIGHTBOX — Vollbild-Bildansicht auf der Produktseite
  // =================================================================
  function initLightbox() {
    if (!document.querySelector('.product__media-list')) return;

    // Overlay einmalig erzeugen
    const overlay = document.createElement('div');
    overlay.className = 'wa-lightbox';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Bildansicht');
    overlay.innerHTML =
      '<button class="wa-lightbox__close" aria-label="Schließen">' +
        '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
          '<path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
        '</svg>' +
      '</button>' +
      '<div class="wa-lightbox__img-wrap">' +
        '<img class="wa-lightbox__img" src="" alt="">' +
      '</div>' +
      '<span class="wa-lightbox__caption">ESC oder Klick zum Schließen</span>';
    document.body.appendChild(overlay);

    var lbImg = overlay.querySelector('.wa-lightbox__img');
    var closeBtn = overlay.querySelector('.wa-lightbox__close');

    function openLightbox(src, alt) {
      lbImg.classList.remove('is-loaded');
      lbImg.src = '';
      overlay.classList.add('is-open');
      document.documentElement.classList.add('wa-lightbox-open');
      // Vollauflösung: width auf 2400 setzen oder Parameter entfernen
      var fullSrc = src.replace(/[?&]width=\d+/, function(m) {
        return m[0] === '?' ? '?width=2400' : '&width=2400';
      });
      if (fullSrc === src) fullSrc = src + (src.indexOf('?') === -1 ? '?width=2400' : '&width=2400');
      lbImg.alt = alt || '';
      lbImg.src = fullSrc;
      lbImg.onload = function() { lbImg.classList.add('is-loaded'); };
    }

    function closeLightbox() {
      overlay.classList.remove('is-open');
      document.documentElement.classList.remove('wa-lightbox-open');
    }

    // Event-Delegation auf der Galerie-Liste
    document.querySelector('.product__media-list').addEventListener('click', function(e) {
      var img = e.target.closest('li:not(.product__media-item--full) img');
      if (!img) return;
      // Dawn-Modal-Öffnung verhindern
      e.preventDefault();
      e.stopPropagation();
      openLightbox(img.currentSrc || img.src, img.alt);
    });

    closeBtn.addEventListener('click', closeLightbox);
    overlay.querySelector('.wa-lightbox__img-wrap').addEventListener('click', closeLightbox);

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) closeLightbox();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLightbox);
  } else {
    initLightbox();
  }

  // =================================================================
  // 7. SCROLL-RESTAURIERUNG — Kollektion-Scroll-Position erhalten
  // =================================================================
  (function initScrollRestore() {
    var SCROLL_KEY = 'wa-scroll-y';
    var URL_KEY    = 'wa-scroll-url';

    // Auf Kollektionsseiten: Scroll-Position beim Klick auf Produkt speichern
    function attachSaveListeners() {
      var grid = document.querySelector('.collection .product-grid, #shopify-section-featured_collection .product-grid');
      if (!grid) return;
      grid.addEventListener('click', function(e) {
        var link = e.target.closest('a[href*="/products/"]');
        if (!link) return;
        sessionStorage.setItem(SCROLL_KEY, String(Math.round(window.scrollY)));
        sessionStorage.setItem(URL_KEY, window.location.href);
      });
    }

    // Scroll-Position wiederherstellen wenn wir auf der gespeicherten URL sind
    function maybeRestore() {
      var savedUrl = sessionStorage.getItem(URL_KEY);
      var savedY   = sessionStorage.getItem(SCROLL_KEY);
      if (!savedUrl || !savedY) return;
      if (window.location.href !== savedUrl) return;

      var y = parseInt(savedY, 10);
      sessionStorage.removeItem(SCROLL_KEY);
      sessionStorage.removeItem(URL_KEY);

      // requestAnimationFrame stellt sicher, dass Layout fertig ist
      requestAnimationFrame(function() {
        window.scrollTo({ top: y, behavior: 'instant' });
      });
    }

    // pageshow deckt sowohl normales Laden als auch bfcache-Restore ab
    window.addEventListener('pageshow', function(e) {
      maybeRestore();
      attachSaveListeners();
    });

    // Falls Seite schon geladen ist (kein pageshow mehr)
    if (document.readyState !== 'loading') {
      maybeRestore();
      attachSaveListeners();
    } else {
      document.addEventListener('DOMContentLoaded', function() {
        maybeRestore();
        attachSaveListeners();
      });
    }
  })();

})();
